import { useEffect, useRef, useState } from 'react';
import ProfileWatchlistService, { ProfileMembershipRow } from './ProfileWatchlistService';
import { LAYER_LABELS } from '../../../watchlists/report/labelMaps';

const LAYER_ORDER = ['activation', 'strategic', 'signal'];
const LAYER_DOT: Record<string, string> = {
    signal: 'bg-primary',
    strategic: 'bg-bs-purple',
    activation: 'bg-success',
};
const LAYER_BADGE: Record<string, string> = {
    signal: 'bg-primary bg-opacity-10 text-primary',
    strategic: 'bg-bs-purple bg-opacity-10 text-primary-emphasis',
    activation: 'bg-success bg-opacity-10 text-success',
};

/**
 * Watchlist membership badge for the alumni profile header (institute admin only).
 * Renders nothing when the profile isn't in any watchlist, or while the fetch is in flight.
 */
const ProfileWatchlistBadge = ({
    studentId,
    instituteId,
    filterInstituteId,
}: {
    studentId: string;
    instituteId: string | number | undefined;
    filterInstituteId?: string | number | null;
}) => {
    const [rows, setRows] = useState<ProfileMembershipRow[] | null>(null);
    const [open, setOpen] = useState(false);
    const lastFetchKey = useRef('');

    useEffect(() => {
        if (!studentId || !instituteId) return;
        const key = `${studentId}-${instituteId}-${filterInstituteId ?? ''}`;
        if (lastFetchKey.current === key) return;
        lastFetchKey.current = key;
        ProfileWatchlistService.getMemberships(instituteId, studentId, filterInstituteId)
            .then((r) => setRows(r.memberships || []))
            .catch(() => setRows([]));
    }, [studentId, instituteId, filterInstituteId]);

    if (!rows || rows.length === 0) return null;

    const priorityLayer = LAYER_ORDER.find((l) => rows.some((r) => r.layer === l)) || 'signal';

    return (
        <span
            className={`d-inline-flex align-items-center gap-1 font-11 font-500 py-1 px-3 rounded-pill position-relative ${LAYER_BADGE[priorityLayer]}`}
            style={{ cursor: 'pointer' }}
            onClick={() => setOpen((o) => !o)}>
            <i className="bi bi-list-check"></i>
            <span className="font-13 font-700">{rows.length}</span> {rows.length === 1 ? 'Watchlist' : 'Watchlists'}
            <i
                className="bi bi-chevron-down font-10"
                style={{ transform: open ? 'rotate(180deg)' : undefined, transition: 'transform 0.2s' }}></i>
            {open && (
                <>
                    <div
                        className="position-fixed top-0 start-0 w-100 h-100"
                        style={{ zIndex: 90 }}
                        onClick={(e) => {
                            e.stopPropagation();
                            setOpen(false);
                        }}></div>
                    <div
                        className="position-absolute top-100 start-0 mt-2 bg-white rounded-3 shadow p-3 text-start"
                        style={{ zIndex: 100, minWidth: 280 }}
                        onClick={(e) => e.stopPropagation()}>
                        <div className="font-10 font-600 text-uppercase text-muted mb-2">Watchlist memberships</div>
                        <div className="d-flex flex-column gap-1" style={{ maxHeight: 300, overflowY: 'auto' }}>
                        {rows.map((r) => (
                            <div key={`${r.layer}-${r.watchlist_code}`} className="d-flex align-items-center gap-2">
                                <span
                                    className={`rounded-circle ${LAYER_DOT[r.layer]}`}
                                    style={{ width: 8, height: 8, flexShrink: 0 }}></span>
                                <div className="flex-1 lh-base">
                                    <div className="font-13 font-500 text-dark">{r.watchlist_label}</div>
                                    <div className="font-11 text-secondary">{LAYER_LABELS[r.layer] || r.layer}</div>
                                </div>
                            </div>
                        ))}
                        </div>
                    </div>
                </>
            )}
        </span>
    );
};

export default ProfileWatchlistBadge;
